import { DataTableColumn } from "mantine-datatable";
import { useMemo } from "react";

import CPExceptionTableCard from "../../../../components/CPExceptionTableCard/CPExceptionTableCard.tsx";
import { DatabaseMetadataPopover } from "../../../../components/DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import Spoiler from "../../../../components/Spoiler/Spoiler.tsx";
import { TableEmptyValue } from "../../../../components/Table/constants.tsx";
import { TableData, TableRow } from "../../../../components/Table/types";
import { toHumanReadableCAResult } from "../../../../types/creditApproval.ts";
import {
  LimitRequest,
  toHumanReadableLimitRequestStatus,
  toHumanReadableTopProductCategory,
} from "../../../../types/limitRequest/limitRequest.ts";
import { toLocalDate } from "../../../../utils/date";
import {
  toCurrencyPrice,
  toLocalNumber,
  toPercent,
} from "../../../../utils/number";
import { toHumanReadable } from "../../../../utils/string";

interface Props {
  data: LimitRequest[] | null | undefined;
}

export const useOngoingOLTableData = ({ data }: Props): TableData => {
  const columns: DataTableColumn<TableRow>[] = [
    {
      accessor: "id",
      hidden: true,
    },
    {
      accessor: "committeeDate",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "CreditApproval",
            fieldName: "date",
          }}
        >
          Committee Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "limitName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "limitName",
          }}
        >
          Limit Name
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 300,
    },
    {
      accessor: "companyName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Customer",
            fieldName: "name",
          }}
        >
          Company Name
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 300,
    },
    {
      accessor: "salesName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Customer",
            fieldName: "salesName",
          }}
        >
          Sales Name
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "salesTeamName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Customer",
            fieldName: "salesTeamName",
          }}
        >
          Sales Team
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "requestStatus",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "requestStatus",
          }}
        >
          Request Status
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "committeeResult",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "CreditApproval",
            fieldName: "result",
          }}
        >
          Committee Result
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "limitApproved",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "limitApproved",
          }}
        >
          Limit Approved
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },

    {
      accessor: "tenorApproved",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "tenorApproved",
          }}
        >
          Tenor Approved
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "pricingApproved",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "pricingApproved",
          }}
        >
          Pricing Approved
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "limitType",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "limitRequestType",
          }}
        >
          Limit Type
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "topProductCategory",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "topProductCategory",
          }}
        >
          TOP Product Category
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "conditionPrecedents",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "conditionPrecedents",
          }}
        >
          Condition Precedents
        </DatabaseMetadataPopover>
      ),
      sortable: false,
      width: 250,
    },
    {
      accessor: "exceptions",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "exceptions",
          }}
        >
          Exceptions
        </DatabaseMetadataPopover>
      ),
      sortable: false,
      width: 250,
    },
    {
      accessor: "committeeNotes",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "CreditApproval",
            fieldName: "notes",
          }}
        >
          Notes
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 400,
    },
  ];

  const rows = useMemo(() => {
    return data?.map((item) => {
      return {
        id: item.id,
        committeeDate: toLocalDate(item?.creditApproval?.date),
        limitName: item.limitName,
        companyName: item.company.name,
        salesName: item.company.salesName,
        salesTeamName: item.company.salesTeamName,
        limitApproved: toCurrencyPrice(item.limitApproved),
        tenorApproved: toLocalNumber(item.tenorApproved),
        pricingApproved: toPercent(item.pricingApproved, "percentage"),
        committeeResult:
          item?.creditApproval?.result &&
          toHumanReadableCAResult(item.creditApproval.result),
        limitType:
          item?.limitRequestType && toHumanReadable(item.limitRequestType),
        topProductCategory:
          item?.topProductCategory &&
          toHumanReadableTopProductCategory(item.topProductCategory),
        conditionPrecedents: item?.conditionPrecedents?.length ? (
          <CPExceptionTableCard data={item?.conditionPrecedents} />
        ) : (
          TableEmptyValue
        ),
        exceptions: item?.exceptions?.length ? (
          <CPExceptionTableCard data={item.exceptions} />
        ) : (
          TableEmptyValue
        ),
        committeeNotes: item?.creditApproval?.notes ? (
          <Spoiler text={item.creditApproval.notes} />
        ) : null,
        requestStatus: toHumanReadableLimitRequestStatus(item.requestStatus),
      };
    });
  }, [data]);

  return [columns, rows];
};
